import { Module } from 'vuex';
import { RootState } from '@/store';
import { Photo, RmaElement } from '@/services/rma-element/rma-element.types';
import { rmaElementsService } from '@/services/rma-element/rma-element.service';

export interface RmaElementsState {
  rmaElementsList: RmaElement[];
  deletedPhotos: (string | File)[];
  rmaElementLoading: boolean;
}

const state: RmaElementsState = {
  rmaElementsList: [],
  deletedPhotos: [],
  rmaElementLoading: false
};

const purchaseItemsModule: Module<RmaElementsState, RootState> = {
  namespaced: true,
  state,
  actions: {
    getRmaElementList({ commit, dispatch }, device_id: number): Promise<void> {
      commit('rmaElementLoading', true);
      return rmaElementsService
        .list(device_id)
        .then(rmaElementsList => {
          if (rmaElementsList.length > 0) {
            commit('rmaElementsList', rmaElementsList);
          }
        })
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
        })
        .finally(() => {
          commit('rmaElementLoading', false);
        });
    },
    generateRmaElement({ commit, dispatch }, opt: { device_id: number; force: boolean }): Promise<void> {
      commit('rmaElementLoading', true);
      return rmaElementsService
        .generate(opt.device_id, opt.force)
        .then(rmaElementsList => {
          commit('rmaElementsList', rmaElementsList);
          dispatch('alert/pushSuccess', 'Rma généré !', { root: true });
        })
        .catch(error => {
          dispatch('alert/pushAlert', { type: 'error', text: error.message, duration: 5 }, { root: true });
        })
        .finally(() => {
          commit('rmaElementLoading', false);
        });
    },
    selectedItem({ commit, dispatch }, rmaElementId: number): Promise<void> {
      commit('rmaElementsSelected', rmaElementId);
      return rmaElementsService
        .selected(rmaElementId)
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
        })
        .finally(() => {
          commit('rmaElementLoading', false);
        });
    },
    updateRmaElement({ commit, dispatch }, rmaElement: RmaElement): Promise<void> {
      return rmaElementsService
        .update(rmaElement)
        .then(rmaElementsList => {
          if (rmaElementsList.length > 0) commit('rmaElementsList', rmaElementsList);
        })
        .catch(error => {
          dispatch('alert/pushError', error, { root: true });
        })
        .finally(() => {
          commit('rmaElementLoading', false);
        });
    },
    editRmaElement: ({ commit }, rmaElement: RmaElement): void => {
      commit('UPDATE_ITEM', rmaElement);
    },
    savePhotos: ({ commit, dispatch }, rmaElement: RmaElement): Promise<void> => {
      commit('rmaElementLoading', true);
      const photosToDelete = state.deletedPhotos.filter((photo: string | File) => typeof photo == 'string');
      const photosToUpload = rmaElement.photos
        .map((photo: Photo) => photo.photos || photo)
        .filter((photo: string | File) => typeof photo !== 'string');

      const deletePromise =
        photosToDelete.length > 0 ? rmaElementsService.deletePhotos(rmaElement.id, photosToDelete) : Promise.resolve();

      return deletePromise
        .then(() => {
          commit('deletedPhotos', []);
          return Promise.all(
            photosToUpload.map((photo: File) =>
              rmaElementsService
                .addPhoto(rmaElement.id, { photo: photo })
                .then(updatedRma => updatedRma && commit('UPDATE_ITEM', updatedRma))
                .catch(error => error && dispatch('alert/pushError', error, { root: true }))
            )
          );
        })
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('rmaElementLoading', false));
    },
    addPhoto: ({ commit }, payload: { rmaElementId: number; photo: string | File }): void => {
      if (!state.rmaElementsList.filter(rma => rma.id == payload.rmaElementId)[0].photos)
        state.rmaElementsList.filter(rma => rma.id == payload.rmaElementId)[0].photos = [];
      commit('addPhoto', payload);
    },
    deletePhoto: ({ commit }, payload: { rmaElementId: number; photo: string | File }): void => {
      commit('addDeletedPhoto', payload.photo);
      commit('deletePhoto', payload);
    }
  },
  mutations: {
    rmaElementsList(state, rmaElementsList: RmaElement[]) {
      state.rmaElementsList = rmaElementsList;
    },
    rmaElementLoading(state, loading: boolean) {
      state.rmaElementLoading = loading;
    },
    rmaElementsSelected(state, rmaElementId: number) {
      state.rmaElementsList
        .filter(rmaElement => rmaElement.id == rmaElementId)
        .map(rmaElement => (rmaElement.select = !rmaElement.select));
    },
    UPDATE_ITEM(state: RmaElementsState, item: RmaElement) {
      const i = state.rmaElementsList.findIndex(is => is.id === item.id);
      if (i !== -1) state.rmaElementsList.splice(i, 1, item);
    },
    addDeletedPhoto: (state, photo) => state.deletedPhotos.push(photo.photos || photo),
    deletedPhotos: (state, photos: (string | File)[]) => (state.deletedPhotos = photos),
    addPhoto: (state, payload: { rmaElementId; photo }) =>
      state.rmaElementsList.filter(rma => rma.id == payload.rmaElementId)[0].photos.push(payload.photo),
    deletePhoto: (state, payload: { rmaElementId; photo }) =>
      state.rmaElementsList
        .filter(rma => rma.id == payload.rmaElementId)[0]
        .photos.splice(
          state.rmaElementsList
            .filter(rma => rma.id == payload.rmaElementId)[0]
            .photos.map(p => p.photos || p)
            .indexOf(payload.photo),
          1
        )
  }
};

export default purchaseItemsModule;
